<template>
	<BaseDrawer
		:show.sync="isShow"
		title="ทดสอบ 7Club+ coupon prefix"
		size="lg"
		data-test-id="drawer-seven-club-prefix-test"
		@close="close"
	>
		<template v-if="isShow">
			<CRow>
				<CCol lg="12" class="mb-4">
					<p class="typo-body-2 color-black-45 mb-0">
						เช็กว่าโค้ดคูปองจากหลังบ้าน CRM ขึ้นต้นด้วย prefix ที่ตั้งไว้จริงไหม
						— <strong>ทั้งหมดทำงานในเบราว์เซอร์ ไม่ได้อัปโหลดไฟล์หรือยิง API</strong>
						และไม่มีผลกับข้อมูลที่บันทึก
					</p>
				</CCol>

				<CCol lg="12" class="mb-3">
					<div class="prefix-box p-3">
						<span class="typo-body-2 color-black-45">prefix ที่จะใช้ทดสอบ</span>
						<span v-if="prefix" class="typo-body-2 font-weight-bold ml-2" data-test-id="testing-prefix">{{ prefix }}</span>
						<span v-else class="typo-body-2 color-alert ml-2">ยังไม่ได้กรอก prefix</span>
					</div>
				</CCol>

				<CCol lg="12">
					<CTextarea
						v-model="codesText"
						label="รายการโค้ด"
						rows="10"
						class="codes-textarea"
						placeholder="วางโค้ดที่นี่ โค้ดละ 1 บรรทัด หรือกดเลือกไฟล์ด้านล่าง"
						data-test-id="codes-textarea"
					/>
					<p class="typo-caption color-black-45 mt-n2 mb-3" data-test-id="codes-status">
						<template v-if="isCalculating">กำลังคำนวณ...</template>
						<template v-else-if="codes.length">อ่านได้ {{ codes.length | numberFormat }} โค้ด</template>
						<template v-else>รองรับได้ถึงหลักแสนโค้ด — ผลจะขึ้นหลังหยุดพิมพ์/วางเสร็จ</template>
					</p>
				</CCol>

				<CCol lg="12" class="mb-4">
					<CButton
						:disabled="isReadingFile"
						class="transparent"
						color="secondary"
						data-test-id="select-file"
						@click="selectFile"
					>
						{{ isReadingFile ? 'Reading...' : 'เลือกไฟล์ (.csv / .txt)' }}
					</CButton>
					<a
						class="typo-body-2 ml-3"
						:href="SAMPLE_FILE_PATH"
						download
					>
						ดาวน์โหลดไฟล์ตัวอย่าง
					</a>
					<span v-if="fileName" class="typo-caption color-black-45 ml-3">
						อ่านจาก {{ fileName }} แล้ว — แก้ในช่องด้านบนต่อได้
					</span>
				</CCol>

				<CCol v-if="result" lg="12">
					<div
						:class="['result-box', 'p-3', isAllMatched ? 'is-pass' : 'is-warning']"
						data-test-id="result"
					>
						<p class="typo-body-2 mb-0">
							<strong>{{ result.matched | numberFormat }}</strong> จาก <strong>{{ result.total | numberFormat }}</strong> โค้ด
							ขึ้นต้นด้วย "{{ prefix }}"
						</p>
						<div v-if="result.unmatched" class="mt-2">
							<p class="typo-caption color-black-45 mb-1">
								โค้ดที่ไม่ตรง {{ result.unmatched | numberFormat }} รายการ
								<template v-if="result.unmatched > result.unmatchedSample.length">
									(แสดง {{ result.unmatchedSample.length }} รายการแรก)
								</template>
							</p>
							<ul class="unmatched-list typo-caption mb-0 pl-3">
								<li v-for="(code, index) in result.unmatchedSample" :key="index">{{ code }}</li>
							</ul>
						</div>
					</div>
				</CCol>
			</CRow>
		</template>
		<template #footer>
			<BaseModalFooter
				text-confirm="Done"
				@onCancel="close"
				@onConfirm="close"
			/>
		</template>
	</BaseDrawer>
</template>

<script>
import debounce from 'lodash/debounce';
import BaseDrawer from '@/components/BaseDrawer.vue';
import BaseModalFooter from '@/components/BaseModalFooter.vue';

// ไฟล์ตัวอย่าง — เสิร์ฟตรงจาก public/ ไม่ผ่าน API (ที่เดียวกับ zip ตัวอย่างของ Upload Content)
const SAMPLE_FILE_PATH = '/assets/templates/seven-club-coupon-prefix-sample.csv';

// จำนวนโค้ดที่ไม่ตรงที่หยิบมาโชว์ — กันรายการยาวเป็นพันบรรทัดในดรอเวอร์
const MAXIMUM_UNMATCHED_SAMPLE = 50;

// หน่วงก่อนคำนวณ — รายการจริงมีได้ถึงหลักแสนโค้ด ถ้าคำนวณทุก keystroke จะหน่วงจนพิมพ์ไม่ได้
const CALCULATE_DEBOUNCE_MS = 300;

/**
 * Drawer ทดสอบว่า prefix ที่ตั้งไว้ตรงกับรายการโค้ดจริงไหม
 *
 * รับโค้ดได้ 2 ทาง: วางเป็นข้อความ หรือเลือกไฟล์ (ไฟล์ถูกอ่านใส่ช่องข้อความเดียวกัน
 * เพื่อให้แก้ต่อได้ และเหลือทางเข้าของข้อมูลทางเดียว)
 *
 * **ไม่ได้อัปโหลดไฟล์และไม่ยิง API** — `FileReader` อ่านในเบราว์เซอร์ล้วน ๆ
 * เปิดแบบ imperative ตามแพตเทิร์น drawer ของโปรเจกต์ (`$refs[...].open()`)
 */
export default {
	name: 'DrawerSevenClubPrefixTest',

	components: {
		BaseDrawer,
		BaseModalFooter,
	},

	props: {
		prefix: {
			type: String,
			default: null,
		},
	},

	data() {
		return {
			SAMPLE_FILE_PATH,
			isShow: false,
			isReadingFile: false,
			isCalculating: false,
			codesText: '',
			// ข้อความที่ "นิ่งแล้ว" — คำนวณจากตัวนี้ ไม่ใช่จาก codesText ตรง ๆ
			settledCodesText: '',
			fileName: null,
		};
	},

	computed: {
		codes() {
			return this.parseCouponCodes(this.settledCodesText);
		},

		result() {
			if (!this.prefix || !this.codes.length) {
				return null;
			}

			return this.checkCodesAgainstPrefix(this.codes, this.prefix);
		},

		isAllMatched() {
			return !!this.result && this.result.matched === this.result.total;
		},
	},

	watch: {
		codesText(value) {
			this.isCalculating = value !== this.settledCodesText;
			this.settleCodesText();
		},
	},

	created() {
		this.settleCodesText = debounce(() => {
			this.settledCodesText = this.codesText;
			this.isCalculating = false;
		}, CALCULATE_DEBOUNCE_MS);
	},

	beforeDestroy() {
		this.settleCodesText.cancel();
	},

	methods: {
		open() {
			this.isShow = true;
		},

		close() {
			this.settleCodesText.cancel();
			this.isShow = false;
			this.codesText = '';
			this.settledCodesText = '';
			this.fileName = null;
			this.isReadingFile = false;
			this.isCalculating = false;
		},

		/**
		 * แยกโค้ดออกจากข้อความ — โค้ดละ 1 บรรทัด
		 * ตัด BOM, บรรทัดว่าง, เครื่องหมายคำพูด, คอลัมน์ที่ 2 เป็นต้นไปของ csv
		 * และตัดหัวตารางทิ้งถ้าบรรทัดแรกหน้าตาเป็นชื่อคอลัมน์
		 *
		 * @param {String} text ข้อความที่วางหรืออ่านมาจากไฟล์
		 * @returns {Array} รายการโค้ด
		 */
		parseCouponCodes(text = '') {
			if (!text) {
				return [];
			}

			// วนรอบเดียวแล้ว push — รายการจริงมีได้ถึงหลักแสนบรรทัด
			// การต่อ .map().filter() จะสร้าง array กลางทางเพิ่มอีกชุดละแสนช่องโดยไม่จำเป็น
			const lines = String(text).replace(/^\uFEFF/, '').split(/\r?\n/);
			const codes = [];

			for (let index = 0; index < lines.length; index++) {
				const commaIndex = lines[index].indexOf(',');
				const cell = commaIndex === -1 ? lines[index] : lines[index].slice(0, commaIndex);
				const code = cell.trim().replace(/^"|"$/g, '');

				if (code.length) {
					codes.push(code);
				}
			}

			if (codes.length && /^[a-z_ ]+$/i.test(codes[0]) && /code|coupon/i.test(codes[0])) {
				return codes.slice(1);
			}

			return codes;
		},

		/**
		 * เทียบรายการโค้ดกับ prefix
		 *
		 * @param {Array} codes รายการโค้ด
		 * @param {String} prefix prefix ที่ตั้งไว้
		 * @returns {Object} {{ total, matched, unmatched, unmatchedSample }}
		 */
		checkCodesAgainstPrefix(codes = [], prefix = '') {
			// เก็บเฉพาะตัวอย่างที่จะเอาไปโชว์ ไม่ได้เก็บ unmatched ทั้งแสนตัวไว้ในหน่วยความจำ
			const unmatchedSample = [];
			let unmatched = 0;

			for (let index = 0; index < codes.length; index++) {
				if (!codes[index].startsWith(prefix)) {
					unmatched += 1;

					if (unmatchedSample.length < MAXIMUM_UNMATCHED_SAMPLE) {
						unmatchedSample.push(codes[index]);
					}
				}
			}

			return {
				total: codes.length,
				matched: codes.length - unmatched,
				unmatched,
				unmatchedSample,
			};
		},

		/**
		 * เลือกไฟล์แล้วอ่านเนื้อไฟล์ใส่ช่องข้อความ — ไม่ได้อัปโหลดไปไหน
		 */
		selectFile() {
			const fileSelector = document.createElement('input');
			fileSelector.setAttribute('type', 'file');
			fileSelector.setAttribute('accept', '.csv,.txt,text/csv,text/plain');
			fileSelector.onchange = () => {
				const file = fileSelector.files.item(0);

				if (!file) {
					return;
				}

				this.isReadingFile = true;
				const reader = new FileReader();

				reader.onload = () => {
					this.codesText = String(reader.result);
					// อ่านไฟล์เสร็จแล้วไม่ต้องรอ debounce — ผู้ใช้ไม่ได้กำลังพิมพ์อยู่
					this.settleCodesText.flush();
					this.fileName = file.name;
					this.isReadingFile = false;
				};

				reader.onerror = () => {
					this.isReadingFile = false;
				};

				reader.readAsText(file);
			};
			fileSelector.click();
		},
	},
};
</script>

<style lang="scss" scoped>
	.prefix-box {
		border-radius: rem(4);
		background-color: $color-gray-100;
	}

	.codes-textarea ::v-deep textarea {
		font-family: monospace;
	}

	.result-box {
		border-radius: rem(4);
		border-left: rem(3) solid $color-gray-400;
		background-color: $color-gray-100;

		&.is-pass {
			border-left-color: $color-success;
		}

		&.is-warning {
			border-left-color: $color-alert;
		}
	}

	.unmatched-list {
		max-height: rem(220);
		overflow-y: auto;
		color: $color-black-70;
	}
</style>
